import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Heart, TrendingUp, Cake, HeartHandshake, CalendarDays, UserX, RefreshCw } from 'lucide-react'
import styles from './CSMAnalytics.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost } from './smApi'

const SM_GET_RELATIONSHIP_INSIGHTS = 'sm_get_relationship_insights'

const CSMAnalytics = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [insights, setInsights] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)

    const loadInsights = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_RELATIONSHIP_INSIGHTS)
        if (res?.status === 'success' && res?.response) {
            setInsights(res.response)
        } else {
            setLoadErrorMessage(res?.message || "Couldn't load relationship insights right now.")
        }
        setLoading(false)
    }, [authCtx, activeStore])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadInsights()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading relationship insights…</span>
            </div>
        )
    }

    if (loadErrorMessage || !insights) {
        return (
            <div className={styles.loadingWrap}>
                <span>{loadErrorMessage || "Couldn't load relationship insights right now."}</span>
                <button type="button" className={styles.retryBtn} onClick={loadInsights}>Retry</button>
            </div>
        )
    }

    const tiles = [
        { label: 'Customers Celebrated', value: insights.customers_celebrated, Icon: Heart },
        { label: 'Wish Rate', value: `${insights.wish_rate}%`, Icon: TrendingUp },
        { label: 'Birthday Wishes', value: insights.birthday_wishes, Icon: Cake },
        { label: 'Anniversary Wishes', value: insights.anniversary_wishes, Icon: HeartHandshake },
        { label: 'Most Active Month', value: insights.most_active_month, Icon: CalendarDays },
        { label: 'Missed Celebrations', value: insights.missed_celebrations, Icon: UserX },
    ]

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Relationship Insights</h1>
                <p className={styles.pageSubtitle}>Not revenue analytics — relationship analytics.</p>
            </div>

            <div className={styles.grid}>
                {tiles.map(({ label, value, Icon }) => (
                    <div key={label} className={styles.tile}>
                        <div className={styles.tileIconWrap}>
                            <Icon size={17} />
                        </div>
                        <div>
                            <div className={styles.tileValue}>{value}</div>
                            <div className={styles.tileLabel}>{label}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default CSMAnalytics
